(() => {
  const root = document.querySelector('[data-elx-story]');
  if (!root) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const revealTargets = root.querySelectorAll('[data-reveal]');
  const hero = root.querySelector('[data-hero]');
  const bridge = root.querySelector('[data-bridge]');
  const lazyVideos = Array.from(root.querySelectorAll('[data-elx-lazy-video]'));
  const assemble = root.querySelector('[data-assemble]');
  const painTransition = root.querySelector('[data-pain-transition]');
  const sceneDots = painTransition ? painTransition.querySelectorAll('[data-scene-dot]') : [];
  const scenePanels = painTransition ? painTransition.querySelectorAll('[data-scene-panel]') : [];
  const painMobileBreakpoint = window.matchMedia('(max-width: 980px)');
  let activePainScene = 1;
  let touchStartX = null;

  const isPainMobile = () => painMobileBreakpoint.matches;

  const setPainScene = (sceneIndex) => {
    if (!painTransition || !scenePanels.length || !sceneDots.length || activePainScene === sceneIndex) return;
    activePainScene = sceneIndex;
    painTransition.classList.toggle('is-scene-2', sceneIndex === 2);

    scenePanels.forEach((panel) => {
      const isActive = Number(panel.getAttribute('data-scene-panel')) === sceneIndex;
      panel.classList.toggle('is-active', isActive);
    });

    sceneDots.forEach((dot) => {
      const isActive = Number(dot.getAttribute('data-scene-dot')) === sceneIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  };

  if (reduceMotion) {
    revealTargets.forEach((target) => target.classList.add('is-visible'));
    const offerProof = root.querySelector('.elx-story__offer-proof');
    if (offerProof) offerProof.classList.add('is-visible');
    const bridgeVideo = root.querySelector('.elx-story__bridge-video');
    if (bridgeVideo && typeof bridgeVideo.pause === 'function') {
      bridgeVideo.pause();
    }
    setPainScene(1);
    return;
  }

  const hydrateVideo = (video) => {
    if (!video || video.dataset.elxVideoHydrated === 'true') return;
    const sources = Array.from(video.querySelectorAll('source[data-src]'));
    if (!sources.length) return;
    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.dataset.elxVideoHydrated = 'true';
    video.load();
  };

  if (lazyVideos.length) {
    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const video = entry.target;
          hydrateVideo(video);
          videoObserver.unobserve(video);
        });
      },
      { rootMargin: '240px 0px' }
    );

    lazyVideos.forEach((video) => videoObserver.observe(video));
  }

  if (sceneDots.length && scenePanels.length) {
    sceneDots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const sceneIndex = Number(dot.getAttribute('data-scene-dot')) || 1;
        setPainScene(sceneIndex);
      });
    });

    painTransition?.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true });

    painTransition?.addEventListener('touchend', (event) => {
      if (touchStartX == null) return;
      const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX;
      const deltaX = touchEndX - touchStartX;
      touchStartX = null;
      if (Math.abs(deltaX) < 42) return;
      const nextScene = deltaX < 0 ? 2 : 1;
      setPainScene(nextScene);
    }, { passive: true });

  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          if (entry.target.classList.contains('elx-story__offer-proof')) {
            entry.target.classList.add('is-visible');
          }
        }
      });
    },
    {
      threshold: 0.24,
      rootMargin: '0px 0px -10% 0px'
    }
  );

  revealTargets.forEach((target) => observer.observe(target));
  const offerProof = root.querySelector('.elx-story__offer-proof');
  if (offerProof) observer.observe(offerProof);

  let ticking = false;

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const updateScrollEffects = () => {
    ticking = false;

    if (hero) {
      const rect = hero.getBoundingClientRect();
      const heroProgress = clamp((0 - rect.top) / (rect.height * 0.9), 0, 1);
      root.style.setProperty('--hero-zoom', (heroProgress * 0.08).toFixed(3));
      root.style.setProperty('--hero-dim', (heroProgress * 0.28).toFixed(3));
    }

    if (bridge) {
      const rect = bridge.getBoundingClientRect();
      const start = window.innerHeight * 0.85;
      const end = -rect.height * 0.2;
      const progress = clamp((start - rect.top) / (start - end), 0, 1);
      root.style.setProperty('--bridge-progress', progress.toFixed(3));
    }

    if (assemble) {
      const rect = assemble.getBoundingClientRect();
      const start = window.innerHeight * 0.8;
      const end = -rect.height * 0.2;
      const progress = clamp((start - rect.top) / (start - end), 0, 1);
      root.style.setProperty('--assemble', progress.toFixed(3));
    }

    if (painTransition) {
      const rect = painTransition.getBoundingClientRect();
      const isMobile = isPainMobile();
      const start = window.innerHeight * (isMobile ? 0.72 : 0.82);
      const end = -rect.height * (isMobile ? 0.26 : 0.18);
      const progress = clamp((start - rect.top) / (start - end), 0, 1);
      const nextScene = activePainScene === 1
        ? (progress >= (isMobile ? 0.7 : 0.62) ? 2 : 1)
        : (progress <= (isMobile ? 0.3 : 0.42) ? 1 : 2);
      setPainScene(nextScene);
    }

  };

  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateScrollEffects);
    }
  };

  updateScrollEffects();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  painMobileBreakpoint.addEventListener('change', () => {
    onScroll();
  });
})();
